import { PlusOutlined, RobotOutlined } from "@ant-design/icons";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate } from "react-router-dom";
import { denyAccess } from "../../../api/access";
import BackButton from "../../../components/BackButton";
import ProfileDropdown from "../../../components/ProfileDropdown";
import { useViewport } from "../../../hooks/useViewport";
import { logout, setAccess } from "../../../store/authSlice";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { AssistantComposer } from "./AssistantComposer";
import { AssistantTurnView } from "./AssistantTurnView";
import { useAssistantChat } from "./useAssistantChat";

const EXAMPLE_KEYS = [
  "monitoring.assistantExample1",
  "monitoring.assistantExample2",
  "monitoring.assistantExample3",
];

// „AI помощник" в модул „Мониторинг" — само за администратора (user id = 1),
// както одитът и „Фискални проблеми". BE-то пази същото (`require_admin`).
// Отговорът е от базата знания и от данните на живо на ТОЗИ ресторант.
const AssistantPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { isNarrow } = useViewport();
  const { turns, pending, ask, reset } = useAssistantChat();
  const scrollRef = useRef<HTMLDivElement>(null);

  const userId = useAppSelector((s) => s.auth.userId);
  const status = useAppSelector((s) => s.auth.status);
  const access = useAppSelector((s) => s.auth.access);
  const names = useAppSelector((s) => s.auth.names);
  const username = useAppSelector((s) => s.auth.username);
  const displayName = names || username || "";

  // Нов въпрос или пристигнал отговор → към дъното на разговора.
  useEffect(() => {
    const element = scrollRef.current;
    if (element) element.scrollTo({ top: element.scrollHeight, behavior: "smooth" });
  }, [turns]);

  const handleLogout = async () => {
    if (access) {
      try {
        await denyAccess();
      } catch (error) {
        console.error("Deny access error:", error);
      }
      dispatch(setAccess(false));
      navigate("/", { replace: true });
    } else {
      await dispatch(logout());
      navigate("/login", { replace: true });
    }
  };

  if (status === "loading") return null;
  if (userId !== 1) return <Navigate to="/" replace />;

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-canvas font-['Plus_Jakarta_Sans',-apple-system,BlinkMacSystemFont,system-ui,sans-serif] text-ink">
      {/* TopBar — същият шаблон като „Мониторинг" (лого + заглавие + мета отдолу) */}
      <div
        className={`flex items-center justify-between gap-[10px] border-b border-shade-2 ${
          isNarrow ? "min-h-[48px] px-[16px] py-[10px]" : "min-h-[60px] px-[28px] py-[11px]"
        }`}
        // Кехлибареният radial gradient сигнализира „ограничен достъп" —
        // зависи от access по време на изпълнение.
        style={{
          background: access
            ? "radial-gradient(ellipse 40% 120% at 100% 50%, var(--color-brand-tint) 0%, transparent 70%), var(--color-surface)"
            : "var(--color-surface)",
        }}
      >
        <div className="flex min-w-0 flex-1 items-center gap-[10px]">
          <BackButton onClick={() => navigate("/monitoring")} />
          <img
            src="/logo-ts.png"
            alt="UnrealSoft"
            className={`shrink-0 select-none object-contain ${isNarrow ? "h-[26px] w-[26px]" : "h-[34px] w-[34px]"}`}
          />
          <div className="min-w-0 flex-1 leading-none">
            <h1
              className={`m-0 overflow-hidden text-ellipsis whitespace-nowrap font-bold leading-[1.2] tracking-[-0.3px] text-ink ${
                isNarrow ? "text-[16px]" : "text-[18px]"
              }`}
            >
              {t("monitoring.assistant")}
            </h1>
            {!isNarrow && (
              <div className="mt-[4px] text-[11px] leading-none tracking-[0.3px] text-muted">
                {t("monitoring.assistantSubtitle")}
              </div>
            )}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-[10px]">
          {turns.length > 0 && (
            <button
              type="button"
              onClick={reset}
              title={t("monitoring.assistantNewConversation")}
              className="inline-flex h-[32px] cursor-pointer items-center gap-[6px] rounded-[8px] border border-shade-3 bg-transparent px-[12px] text-[12.5px] font-semibold text-body transition-colors duration-150 hover:bg-shade-1 hover:text-ink"
            >
              <PlusOutlined className="!text-[12px]" />
              {!isNarrow && t("monitoring.assistantNewConversation")}
            </button>
          )}
          {username && (
            <ProfileDropdown
              displayName={displayName}
              username={username}
              access={access}
              isNarrow={isNarrow}
              onLogout={handleLogout}
            />
          )}
        </div>
      </div>

      {/* Разговорът */}
      <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto">
        <div
          className={`flex min-h-full flex-col gap-[22px] ${isNarrow ? "px-[14px] py-[16px]" : "px-[28px] py-[24px]"}`}
        >
          {turns.length === 0 ? (
            <div className="flex flex-1 flex-col items-center justify-center gap-[14px] text-center">
              <span className="flex h-[56px] w-[56px] items-center justify-center rounded-[16px] border border-[rgba(217,119,6,0.16)] bg-[linear-gradient(135deg,var(--color-brand-tint)_0%,var(--color-brand-tint2)_100%)]">
                <RobotOutlined className="!text-[26px] !text-brand-deep-ink" />
              </span>
              <div className="text-[18px] font-bold leading-[1.2] tracking-[-0.3px] text-ink">
                {t("monitoring.assistantEmptyTitle")}
              </div>
              <div className="max-w-[440px] text-[13.5px] leading-[1.55] text-body">
                {t("monitoring.assistantEmpty")}
              </div>
              <div className="mt-[6px] flex flex-wrap justify-center gap-[8px]">
                {EXAMPLE_KEYS.map((key) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => void ask(t(key))}
                    className="cursor-pointer rounded-full border border-shade-3 bg-surface px-[12px] py-[6px] text-[12.5px] font-medium text-body transition-colors duration-150 hover:border-brand-ink hover:text-ink"
                  >
                    {t(key)}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            turns.map((turn) => <AssistantTurnView key={turn.key} turn={turn} isNarrow={isNarrow} />)
          )}
        </div>
      </div>

      {/* Полето — долу; половин safe-area отстъп (index.html е с viewport-fit=cover) */}
      <div
        className={`border-t border-shade-2 bg-canvas ${
          isNarrow
            ? "px-[12px] pt-[10px] pb-[calc(10px+env(safe-area-inset-bottom,0px)*0.5)]"
            : "px-[28px] pt-[14px] pb-[calc(16px+env(safe-area-inset-bottom,0px)*0.5)]"
        }`}
      >
        <AssistantComposer onAsk={(question) => void ask(question)} busy={pending} isNarrow={isNarrow} />
      </div>
    </div>
  );
};

export default AssistantPage;
